/**
 * regulatoryTemplate — modèle de PLACEMENT réglementaire (.lfgr).
 *
 * Un modèle capture, pour chaque calque réglementaire (regBlock) d'une étiquette
 * de référence : sa position, sa taille, sa mise en forme (police, taille,
 * couleur, alignement, contour, ombre…) ET son texte de départ (guide visuel
 * quand on rouvre le modèle pour l'ajuster).
 *
 * À l'APPLICATION sur un autre import réglementaire : on matche les calques par
 * rôle (comme le « Rafraîchir »), et on applique placement + mise en forme du
 * modèle en CONSERVANT le nouveau texte/contenu. Autrement dit :
 *   contenu = import courant · style + position = modèle.
 */
import { hauteurTexteEstimee } from './hauteurTexte.js';
import { regLayerKey, lotAvecZones, gardeFauteDeZones, estFicheMasqueeDEssai, estJumeauDEssai } from './regulatoryKey';
import { styleEffectifTexte, styleDominantTexte, aDesStylesPartiels,
         retirerMarksDeStyle, poserAlignementDansContenu } from './richTextModel';

// Ré-export pour les consommateurs historiques de ce module.
export { regLayerKey };

// Attributs de POSITION (tous types de calques).
const PLACEMENT_KEYS = ['x', 'y', 'width', 'height', 'rotation', 'opacity'];

// Attributs de MISE EN FORME des calques NON-TEXTE (pictogrammes, repères).
// Un picto n'a ni police ni alignement, mais l'inspecteur lui propose bien une
// ombre portée — qui se perdait intégralement au passage par un modèle, alors
// que le message annonçait « reformatés ».
const SHAPE_STYLE_KEYS = [
    'shadowEnabled', 'shadowColor', 'shadowDistance', 'shadowAngle',
    'shadowSize', 'shadowSpread', 'shadowOpacity', 'shadowInset',
];

// Attributs de MISE EN FORME (calques texte uniquement).
const STYLE_KEYS = [
    'font', 'size', 'color', 'fontWeight', 'fontStyle', 'textAlign',
    'lineHeight', 'letterSpacing', 'textTransform',
    'fillType', 'gradient',
    'strokeEnabled', 'strokeColor', 'strokeWidth', 'strokeType', 'strokeGradient',
    'shadowEnabled', 'shadowColor', 'shadowDistance', 'shadowAngle',
    'shadowSize', 'shadowSpread', 'shadowOpacity', 'shadowInset',
];

/** Retire les marks de style inline (textStyle) d'un content Tiptap, pour que
 *  le style du calque (issu du modèle) prime sur d'éventuels styles hérités. */
function stripStyleMarks(content) {
    if (!content || typeof content !== 'object') return content;
    const clone = JSON.parse(JSON.stringify(content));
    const walk = (node) => {
        if (!node || typeof node !== 'object') return;
        if (Array.isArray(node.marks)) {
            node.marks = node.marks.filter((m) => m && m.type !== 'textStyle');
            if (node.marks.length === 0) delete node.marks;
        }
        if (Array.isArray(node.content)) node.content.forEach(walk);
    };
    walk(clone);
    return clone;
}

/**
 * Style d'un calque texte tel qu'il est RÉELLEMENT PEINT, ramené à plat.
 *
 * Un calque édité au double-clic porte sa couleur et sa police dans les marks
 * de son rich text, pas dans `el.color` / `el.font` — et au rendu, ce sont les
 * marks qui gagnent. Lire les propriétés à plat, c'est donc enregistrer dans le
 * modèle un style que personne n'a jamais vu : le #000000 posé par le builder.
 * *C'est exactement ce qui faisait revenir en noir un code UFI passé en rouge à
 * l'application du modèle (retour utilisateur du 2026-08-21).*
 *
 * On normalise donc à la SOURCE : la valeur uniforme des marks est promue en
 * propriété, et la mark correspondante retirée du contenu enregistré. Le
 * fichier .lfgr ne contient plus qu'une seule vérité, et les deux portes qui le
 * consomment (`applyRegulatoryTemplate` et `instantiateRegulatoryTemplate`) ne
 * peuvent plus produire deux apparences différentes.
 *
 * Un style MIXTE (plusieurs couleurs dans le même calque) ne se réduit pas à une
 * propriété : on garde alors les marks telles quelles — détruire l'information
 * serait pire — et on le signale à l'appelant via `mixte`, pour qu'il le dise.
 *
 * @returns {{color?:string, font?:string, size?:number, content?:object,
 *            mixte:{color:boolean,font:boolean,size:boolean}}}
 */
function styleNormalise(e) {
    const reel = styleEffectifTexte(e);
    const aPromouvoir = ['color', 'font', 'size'].filter((c) => !reel.mixte[c]);
    const sortie = {
        color: reel.color,
        font: reel.font,
        size: reel.size,
        content: e.content ? retirerMarksDeStyle(e.content, aPromouvoir) : e.content,
        mixte: reel.mixte,
    };
    // Style non uniforme : aucune propriété ne peut le décrire. On garde les
    // marks (le contenu enregistré les porte, et le modèle les rejouera à
    // l'identique si le texte est le même), mais on retient tout de même la
    // valeur MAJORITAIRE comme propriété de repli — sur un texte différent, les
    // mots ne sont plus les mêmes et il faut bien poser quelque chose.
    if (e.content && (reel.mixte.color || reel.mixte.font || reel.mixte.size)) {
        const dom = styleDominantTexte(e.content);
        if (reel.mixte.color && dom.color) sortie.color = dom.color;
        if (reel.mixte.font && dom.font) sortie.font = dom.font;
        if (reel.mixte.size && dom.size) sortie.size = dom.size;
    }
    return sortie;
}

/**
 * Extrait un modèle (.lfgr) depuis les calques d'une étiquette.
 * @returns {{type, version, canvas, layers}}
 */
export function extractRegulatoryTemplate(elements, canvas) {
    // Rôles portés par plusieurs calques (UFI répété sur un rabat, par exemple).
    // La clé n'est pas unique : le signaler à l'appelant vaut mieux que de laisser
    // le dernier écraser les autres en silence à l'application.
    const compteParCle = {};
    const stylesMixtes = [];

    const layers = (elements || [])
        // Le distributeur masqué d'une étiquette d'essai n'est pas un emplacement : son jumeau, au même cadre, porte le
        // gabarit du rôle (sinon le modèle poserait un second distributeur, visible, sur toute étiquette).
        .filter((e) => e && e.regBlock && !estFicheMasqueeDEssai(e))
        .map((e) => {
            const cle = regLayerKey(e);
            compteParCle[cle] = (compteParCle[cle] || 0) + 1;
            const t = { key: cle, role: e.regBlock.role, layerType: e.type };
            if (e.name) t.name = e.name;
            if (e.regBlock.pictogramCode) t.pictogramCode = e.regBlock.pictogramCode;
            for (const k of PLACEMENT_KEYS) if (e[k] !== undefined) t[k] = e[k];
            if (e.type === 'text') {
                for (const k of STYLE_KEYS) if (e[k] !== undefined) t[k] = e[k];
                // Le style RÉELLEMENT PEINT écrase ce qui vient d'être copié à
                // plat : sans cela, une couleur posée au double-clic n'entre
                // jamais dans le modèle (cf. styleNormalise).
                const st = styleNormalise(e);
                if (st.color !== undefined) t.color = st.color;
                if (st.font !== undefined) t.font = st.font;
                if (st.size !== undefined) t.size = st.size;
                if (st.mixte.color || st.mixte.font || st.mixte.size) {
                    stylesMixtes.push(cle);
                }
                // `text` et `content` ne sont plus un simple guide visuel : ils
                // servent à REPRODUIRE la mise en forme mot à mot quand le texte
                // du nouvel import est identique (mentions fixes, phrases EUH,
                // « PG : 50 »…). C'est le seul cas où un mot en rouge dans une
                // phrase noire peut revenir tel quel — sur un texte différent,
                // les mots ne sont plus les mêmes et rien ne dit où poser le rouge.
                if (e.text !== undefined) t.text = e.text;
                if (st.content !== undefined) t.content = st.content;
                if (e.content && aDesStylesPartiels(e.content)) t.stylePartiel = true;
            } else {
                // Un pictogramme n'est pas du texte, mais son ombre portée est
                // bien réglable dans l'inspecteur, et se perdait entièrement.
                for (const k of SHAPE_STYLE_KEYS) if (e[k] !== undefined) t[k] = e[k];
            }
            return t;
        });

    const clesEnDouble = Object.keys(compteParCle).filter((k) => compteParCle[k] > 1);
    return {
        type: 'lfgr',
        version: 1,
        canvas: { width_mm: canvas?.width_mm, height_mm: canvas?.height_mm },
        createdAt: null, // stampé après coup (Date indisponible ici côté pur)
        layers,
        // Diagnostics, à rapporter à l'utilisateur — pas des données du modèle.
        _clesEnDouble: clesEnDouble,
        _stylesMixtes: [...new Set(stylesMixtes)],
    };
}

/**
 * Applique un modèle aux calques réglementaires d'une liste d'éléments.
 *
 * Règle : **contenu = étiquette courante · placement et mise en forme = modèle**,
 * avec une exception qui compte — quand le texte du modèle et celui de
 * l'étiquette sont IDENTIQUES, la mise en forme est reproduite mot à mot,
 * marks comprises. C'est le seul moyen de faire revenir un mot en rouge dans
 * une phrase noire : sur un texte différent, les mots ne sont plus les mêmes et
 * rien ne dit où reposer le rouge — on applique alors la valeur majoritaire et
 * on le signale.
 *
 * Le modèle sait aussi RECRÉER les exemplaires manquants : si l'étiquette de
 * référence portait deux fois l'UFI (dupliqué pour un rabat) et que le nouvel
 * import n'en fournit qu'un, le second est reconstitué, au placement du modèle,
 * avec le texte du premier.
 *
 * Un rôle que le modèle n'a JAMAIS vu — un pictogramme d'un autre code GHS, une
 * mention apparue dans la fiche depuis — n'est plus laissé où l'import l'avait
 * posé : il adopte le bloc (cf. `adopterOrphelins`), et ressort dans `adoptes`
 * pour que l'utilisateur sache que cette place-là, c'est le logiciel qui l'a
 * choisie.
 *
 * @returns {{elements, patches, creations, applied, matchedKeys, missingInLabel,
 *            missingInTemplate, adoptes, nonPlaces, sansGabarit, stylesNonRejoues}}
 *   - patches   : map { [id]: partial } à appliquer au store (mutation atomique)
 *   - creations : calques à AJOUTER (sans id — l'appelant en attribue un)
 *   - adoptes   : rôles rangés d'office, faute de gabarit
 *   - nonPlaces : rôles que rien ne permettait de ranger, restés en place
 */
export function applyRegulatoryTemplate(template, elements) {
    // Index en LISTES, pas en valeur unique : un rôle peut être porté par
    // plusieurs calques (UFI répété sur un rabat, nicotine sur les deux faces).
    // Avec `byKey[key] = t`, le dernier gagnait et les homonymes recevaient tous
    // le MÊME placement — ils se retrouvaient empilés au pixel près, et le
    // message annonçait tranquillement « 2 calques replacés ».
    const byKey = {};
    for (const t of (template?.layers || [])) {
        if (!t.key) continue;
        (byKey[t.key] = byKey[t.key] || []).push(t);
    }

    let applied = 0;
    const matchedKeys = [];
    const patches = {};
    const stylesNonRejoues = [];
    // Compteur par clé : le i-ème calque d'un rôle reçoit le i-ème gabarit de ce
    // rôle, dans l'ordre du document.
    const vus = {};
    const modeleParCle = {};   // un calque source par rôle, pour les recréations
    const orphelins = [];      // calques qu'aucun gabarit ne couvre (cf. adopterOrphelins)

    for (const e of (elements || [])) {
        if (!e || !e.regBlock) continue;
        const key = regLayerKey(e);
        const liste = key && byKey[key];
        if (!liste) { orphelins.push(e); continue; }  // rôle absent du modèle
        if (!modeleParCle[key]) modeleParCle[key] = e;
        const rang = vus[key] || 0;
        vus[key] = rang + 1;
        const t = liste[rang];
        if (!t) continue;     // plus de gabarit pour cet exemplaire (cf. sansGabarit)
        patches[e.id] = gabaritVersPatch(t, e, stylesNonRejoues, key);
        applied++;
        matchedKeys.push(key);
    }

    // ── Rôles que le modèle n'a jamais vus : ils ADOPTENT le bloc ──
    const adoptes = adopterOrphelins(template, orphelins, vus, patches);

    const out = (elements || []).map((e) => (e && patches[e.id] ? { ...e, ...patches[e.id] } : e));

    // ── Exemplaires du modèle que l'étiquette n'a pas : on les RECRÉE ──
    // Le contenu vient du calque de même rôle déjà présent (c'est le texte du
    // produit courant qu'il faut répéter, pas celui de l'étiquette de référence).
    const creations = [];
    const sansGabarit = [];
    for (const [key, liste] of Object.entries(byKey)) {
        const presents = vus[key] || 0;
        if (presents > liste.length) { sansGabarit.push(key); continue; }
        const source = modeleParCle[key];
        if (!source) continue;   // rôle absent de l'étiquette → rien à dupliquer
        for (let i = presents; i < liste.length; i++) {
            const t = liste[i];
            const copie = JSON.parse(JSON.stringify(source));
            delete copie.id;
            delete copie.linkedId;
            creations.push({ ...copie, ...gabaritVersPatch(t, source, stylesNonRejoues, key) });
        }
    }

    // Diagnostic : rôles du modèle non présents dans l'étiquette (et inversement).
    const labelKeys = new Set((elements || []).map(regLayerKey).filter(Boolean));
    const templateKeys = Object.keys(byKey);
    const missingInLabel = templateKeys.filter((k) => !labelKeys.has(k));
    const missingInTemplate = [...labelKeys].filter((k) => !templateKeys.includes(k));

    return {
        elements: out, patches, creations, applied, matchedKeys,
        missingInLabel, missingInTemplate,
        // Rôles absents du modèle que le bloc a ADOPTÉS : le logiciel leur a
        // choisi une place, il ne l'a pas lue dans le modèle. À dire.
        adoptes: [...new Set(adoptes)],
        // Rôles absents du modèle que rien ne permettait de ranger : ils sont
        // restés là où l'import les avait posés.
        nonPlaces: missingInTemplate.filter((k) => !adoptes.includes(k)),
        // Rôles présents en PLUS d'exemplaires ici que dans le modèle.
        sansGabarit: [...new Set(sansGabarit)],
        // Rôles dont le modèle portait une mise en forme mot à mot que le
        // nouveau texte ne permet pas de rejouer telle quelle.
        stylesNonRejoues: [...new Set(stylesNonRejoues)],
    };
}


/**
 * Mise en forme et point d'ancrage du CORPS DE TEXTE d'un modèle.
 *
 * Une mention qui arrive sans gabarit est presque toujours une PHRASE LONGUE —
 * une notice, une composition, une liste d'allergènes. La colonne où la ranger
 * est donc celle qui accueille déjà les phrases longues : la famille de mise en
 * forme du gabarit texte LE PLUS LARGE, et sa plus grande largeur.
 *
 * On a d'abord pris la mise en forme la plus FRÉQUENTE. Sur un bloc réel, deux
 * familles se retrouvaient à trois gabarits chacune et c'est l'ordre des clés
 * qui tranchait : les quatre mentions adoptées sont tombées dans une colonne de
 * 9,8 mm, en blanc. Le nombre ne dit rien de la place disponible ; la largeur,
 * si. (Mesuré sur le retour du 2026-09-17, avec ses fichiers.)
 *
 * Une « famille » = même police, même corps, même couleur, même alignement. Le
 * point d'ancrage est le bas du gabarit le plus bas de cette famille : c'est
 * sous lui qu'on empile. On adopte une place existante, on n'en invente pas.
 *
 * @returns {{style:object, x:number, width:number, y:number, ecart:number,
 *            size:number}|null} null si le modèle n'a aucun gabarit texte.
 */
function ancrageTexteDuModele(template) {
    const textes = (template?.layers || []).filter((t) => t && t.layerType === 'text');
    if (!textes.length) return null;
    const signature = (t) => JSON.stringify([t.font, t.size, t.color, t.textAlign]);
    // Le plus large — à largeur égale, le premier du document, pour que deux
    // applications du même modèle donnent toujours le même résultat.
    const large = textes.reduce((m, t) => ((t.width || 0) > (m.width || 0) ? t : m), textes[0]);
    const famille = textes.filter((t) => signature(t) === signature(large));
    const bas = famille.reduce((m, t) =>
        ((t.y || 0) + (t.height || 0) > (m.y || 0) + (m.height || 0) ? t : m), famille[0]);

    const style = {};
    for (const k of STYLE_KEYS) if (bas[k] !== undefined) style[k] = bas[k];
    return {
        style,
        x: large.x !== undefined ? large.x : 0,
        width: famille.reduce((m, t) => Math.max(m, t.width || 0), 0) || large.width,
        y: (bas.y || 0) + (bas.height || 0),
        // Un demi-corps entre deux mentions empilées : assez pour qu'elles ne se
        // touchent pas, assez peu pour que le bloc reste un bloc. 25,4/72 = mm/pt.
        ecart: (bas.size || 3) * (25.4 / 72) * 0.5,
        size: bas.size,
    };
}


/** Deux rectangles se chevauchent-ils ? (mm, coins haut-gauche) */
function chevauche(a, b) {
    return !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y);
}


/**
 * Où poser un pictogramme de plus que le modèle n'en prévoyait.
 *
 * On prolonge la rangée — c'est la lecture naturelle d'une suite de losanges —
 * mais en REGARDANT ce qui est déjà là : à droite, puis à gauche, puis dessous,
 * puis dessus, et on retient la première place qui ne recouvre rien et qui tient
 * dans le plan de travail. Un losange posé sur les mentions légales les rend
 * illisibles, et ça ne se voit sur aucune coordonnée.
 *
 * Aucune place propre ? On prolonge quand même vers la droite : une place
 * visiblement fausse se corrige d'un glissement, un silence ne se corrige pas.
 */
function placeLibrePourPicto(pictos, occupe, gabarit, rang, canvas) {
    const w = gabarit.width || PICTO_MIN_MM;
    const h = gabarit.height || PICTO_MIN_MM;
    const premier = pictos[0] || gabarit;
    const dernier = pictos[pictos.length - 1] || gabarit;
    // Pas de la rangée : l'écart réel entre deux losanges du modèle, à défaut
    // leur largeur plus un millimètre de respiration.
    const pasX = pictos.length > 1
        ? Math.abs((pictos[1].x || 0) - (pictos[0].x || 0)) : (w + 1);
    const pasY = h + 1;
    const candidats = [
        { x: (dernier.x || 0) + rang * pasX, y: dernier.y || 0 },
        { x: (premier.x || 0) - rang * pasX, y: premier.y || 0 },
        { x: premier.x || 0, y: (dernier.y || 0) + rang * pasY },
        { x: premier.x || 0, y: (premier.y || 0) - rang * pasY },
    ];
    const dansLePlan = (c) => !canvas?.width_mm || !canvas?.height_mm
        || (c.x >= -0.5 && c.y >= -0.5
            && c.x + w <= canvas.width_mm + 0.5 && c.y + h <= canvas.height_mm + 0.5);
    const libre = (c) => dansLePlan(c) && !occupe.some((o) => chevauche({ x: c.x, y: c.y, w, h }, o));
    for (const c of candidats) if (libre(c)) return c;

    // La rangée est bouchée. Plutôt que d'écraser une mention, on cherche la
    // place libre la PLUS PROCHE du dernier losange : les pictogrammes doivent
    // rester groupés, mais un groupe un peu lâche vaut mieux qu'un groupe illisible.
    if (canvas?.width_mm && canvas?.height_mm) {
        const PAS = 1;
        let meilleur = null, meilleureDist = Infinity;
        for (let y = 0; y + h <= canvas.height_mm + 0.5; y += PAS) {
            for (let x = 0; x + w <= canvas.width_mm + 0.5; x += PAS) {
                if (!libre({ x, y })) continue;
                const d = Math.hypot(x - (dernier.x || 0), y - (dernier.y || 0));
                if (d < meilleureDist) { meilleureDist = d; meilleur = { x, y }; }
            }
        }
        if (meilleur) return meilleur;
    }
    // Étiquette pleine : on prolonge quand même la rangée. L'appelant signale
    // ce qui sort du plan de travail, et une place visiblement fausse se corrige
    // d'un glissement — un silence, non.
    return candidats[0];
}


/**
 * Range les calques réglementaires qu'AUCUN gabarit ne couvre.
 *
 * Deux cas, tous deux courants dès qu'on change de produit :
 *   - un pictogramme d'un AUTRE code GHS (changer de produit change les
 *     dangers, donc les losanges) ;
 *   - une mention que la fiche n'apportait pas encore quand le modèle a été
 *     enregistré (composition, distributeur et notices datent du 2026-09-10).
 *
 * Jusqu'ici ils restaient exactement là où l'import les avait posés : un pavé
 * Arial 7 pt noir en travers d'une étiquette entièrement composée en Lato
 * 2,7 pt, pendant que le message annonçait des calques « replacés & reformatés ».
 * *Retour utilisateur du 2026-09-17 : cinq mentions sur treize dans ce cas,
 * l'étiquette était inutilisable et le modèle passait pour fautif.*
 *
 * Deux règles, et aucune devinette :
 *   - un PICTOGRAMME prend la place d'un pictogramme du modèle resté libre, dans
 *     l'ordre du document. Le modèle dit « le premier losange va ici » : c'est
 *     cela qui porte l'intention, pas le code GHS. Quand il n'y a plus de place
 *     libre, il PROLONGE la rangée, au pas qui sépare déjà deux losanges.
 *   - une MENTION TEXTE adopte la mise en forme du corps du bloc et se range
 *     dessous, une ligne après l'autre.
 *
 * Ce qui reste sans solution — un pictogramme quand le modèle n'en a aucun — ne
 * bouge pas : une place inventée sur du réglementaire est pire qu'une place
 * visiblement fausse.
 *
 * @param {object[]} orphelins - calques sans gabarit, dans l'ordre du document
 * @param {object} vus - clés déjà consommées par une correspondance exacte
 * @param {object} patches - MUTÉ : les patches calculés y sont ajoutés
 * @returns {string[]} clés adoptées, à rapporter à l'utilisateur
 */
function adopterOrphelins(template, orphelins, vus, patches) {
    if (!orphelins || !orphelins.length) return [];
    const adoptes = [];
    const tousPictos = (template?.layers || []).filter((t) => t && t.role === 'pictogram');
    const pictosLibres = tousPictos.filter((t) => !(vus[t.key] > 0));
    // Ce que le modèle occupe déjà : un losange surnuméraire ne doit pas s'y
    // poser dessus. Le premier jet prolongeait la rangée sans regarder, et
    // posait le second pictogramme en travers des mentions — invisible sur les
    // coordonnées, flagrant dès qu'on rend l'étiquette.
    const occupe = (template?.layers || [])
        .filter((t) => t && t.width !== undefined && t.height !== undefined)
        .map((t) => ({ x: t.x || 0, y: t.y || 0, w: t.width, h: t.height }));
    let prolonges = 0;

    const ancre = ancrageTexteDuModele(template);
    let curseurY = ancre ? ancre.y + ancre.ecart : 0;

    for (const e of orphelins) {
        if (e.type !== 'text') {
            const libre = pictosLibres.shift();
            const t = libre || tousPictos[tousPictos.length - 1];
            if (!t) continue;              // le modèle n'a aucun losange : on n'invente pas
            const patch = {};
            for (const k of PLACEMENT_KEYS) if (t[k] !== undefined) patch[k] = t[k];
            for (const k of SHAPE_STYLE_KEYS) if (t[k] !== undefined) patch[k] = t[k];
            if (!libre) {
                prolonges++;
                const place = placeLibrePourPicto(tousPictos, occupe, t, prolonges,
                                                  template?.canvas);
                patch.x = place.x; patch.y = place.y;
            }
            occupe.push({ x: patch.x || 0, y: patch.y || 0,
                          w: patch.width || PICTO_MIN_MM, h: patch.height || PICTO_MIN_MM });
            // Pas de `name` : « REG · Pictogramme GHS07 » collé sur un GHS02
            // serait un mensonge, et c'est par ce nom qu'on relit une étiquette.
            patches[e.id] = patch;
            adoptes.push(regLayerKey(e));
            continue;
        }
        if (!ancre) continue;              // modèle sans aucune mention texte
        // La hauteur posée par l'import est calibrée pour SON corps (5,5 pt) : la
        // reprendre telle quelle sur un bloc composé en 3 pt ouvrirait un cadre
        // deux fois trop haut, et le suivant se rangerait dans le vide.
        const hauteur = (e.height && e.size && ancre.size)
            ? Math.max(0.5, e.height * (ancre.size / e.size))
            : (e.height || 3);
        const patch = { ...ancre.style, x: ancre.x, y: curseurY, height: hauteur };
        if (ancre.width !== undefined) patch.width = ancre.width;
        // Le calque sort d'un import : ses styles inline contrediraient la mise
        // en forme adoptée, et l'alignement vit dans les attributs de paragraphe.
        if (e.content) {
            const nettoye = stripStyleMarks(e.content);
            patch.content = ancre.style.textAlign
                ? poserAlignementDansContenu(nettoye, ancre.style.textAlign) : nettoye;
        }
        patches[e.id] = patch;
        curseurY += hauteur + ancre.ecart;
        adoptes.push(regLayerKey(e));
    }
    return adoptes;
}

/** Traduit un gabarit du modèle en patch pour un calque donné. */
function gabaritVersPatch(t, e, stylesNonRejoues, key) {
    const patch = {};
    for (const k of PLACEMENT_KEYS) if (t[k] !== undefined) patch[k] = t[k];
    if (t.name) patch.name = t.name;
    if (e.type !== 'text') {
        for (const k of SHAPE_STYLE_KEYS) if (t[k] !== undefined) patch[k] = t[k];
        return patch;
    }
    for (const k of STYLE_KEYS) if (t[k] !== undefined) patch[k] = t[k];

    // Texte identique → reproduction EXACTE, y compris les couleurs mot à mot.
    if (t.text !== undefined && t.text === e.text && t.content) {
        patch.content = JSON.parse(JSON.stringify(t.content));
        return patch;
    }
    // Texte différent : la mise en forme du modèle prime, on neutralise les
    // styles inline du calque cible. L'ALIGNEMENT vit dans les attributs de
    // paragraphe, que `stripStyleMarks` laisse passer : sans la ligne suivante,
    // un calque déjà édité au double-clic ignorait l'alignement du modèle à
    // l'écran tout en l'appliquant au PDF.
    if (e.content) {
        const nettoye = stripStyleMarks(e.content);
        patch.content = t.textAlign ? poserAlignementDansContenu(nettoye, t.textAlign) : nettoye;
    }
    if (t.stylePartiel) stylesNonRejoues.push(key);
    return patch;
}

/** Taille mini d'un pictogramme GHS (conditionnement ≤ 3 L), en mm. La même que
 *  celle posée à l'import par le builder — en dessous, la mention n'est plus
 *  conforme, et c'est le genre de chose qu'un logiciel doit dire tout haut. */
export const PICTO_MIN_MM = 16;

/**
 * Corps en dessous duquel une mention réglementaire cesse d'être lisible à
 * l'impression. Ce n'est PAS un seuil légal — la réglementation raisonne en
 * hauteur d'x et dépend du conditionnement — mais un garde-fou : réduire un
 * modèle d'une 130×130 à une 40×25 ramène un texte de 9 pt à 2 pt, et personne
 * ne veut découvrir ça sur le carton imprimé.
 */
export const CORPS_MIN_PT = 5;

/**
 * La typographie des mentions que LabelForge pose (le bloc de l'import, la disposition de
 * l'agent) : Arial Narrow 5,5 pt, et 5 pt (CORPS_MIN_PT) pour le seul repli d'une ligne qui ne
 * tient pas (un UFI, un e-mail), jamais en dessous.
 * *Décision de Redouane du 24/10/2026 : « on anticipe l'Omnibus VI ». Le CLP révisé par
 * l'Omnibus VI (texte voté au Parlement le 22/10/2026, T10-0362/2026, procédure 2025/0531(COD),
 * en attente du Conseil et de la publication) demande une hauteur d'x de 0,9 mm jusqu'à 125 ml
 * (annexe I, 1.2.1.5.4) : tous nos flacons, du 10 au 100 ml. Arial Narrow a une hauteur d'x de
 * 0,5283 corps (hauteursX.js) : 5,5 pt donnent 1,02 mm, la marge pour l'impression ; 5 pt,
 * 0,93 mm. Avant, 7 pt (1,30 mm) visaient le 1,2 mm du règlement 2025/2439.*
 * Si l'Omnibus n'était pas publié avant le 1er janvier 2028, c'est le 1,2 mm qui s'appliquerait :
 * repasser à 7 pt (6,5 pt en repli).
 */
export const POLICE_MENTIONS = 'Arial Narrow';
export const CORPS_MENTIONS_PT = 5.5;

/** Facteurs de mise à l'échelle d'un format vers un autre. */
function facteurs(depuis, vers) {
    const rx = (depuis?.width_mm && vers?.width_mm) ? vers.width_mm / depuis.width_mm : 1;
    const ry = (depuis?.height_mm && vers?.height_mm) ? vers.height_mm / depuis.height_mm : 1;
    return { rx, ry, ru: Math.min(rx, ry) };
}

/** Vrai si le modèle a été conçu pour un autre format que celui du canevas. */
export function formatDifferent(template, canvas) {
    const t = template?.canvas;
    if (!t?.width_mm || !t?.height_mm || !canvas?.width_mm || !canvas?.height_mm) return false;
    return Math.abs(t.width_mm - canvas.width_mm) > 0.5 || Math.abs(t.height_mm - canvas.height_mm) > 0.5;
}

/**
 * Met un modèle à l'échelle d'un autre format d'étiquette.
 *
 * Deux facteurs, et c'est volontaire :
 *   - les POSITIONS suivent chaque axe (rx pour x, ry pour y), sinon le bloc se
 *     tasse dans un coin au lieu d'occuper la nouvelle étiquette ;
 *   - les TAILLES suivent le plus petit des deux (ru), pour qu'un pictogramme
 *     reste carré et qu'un corps de texte ne s'étire pas.
 *
 * `adapterCorps` décide du sort des corps de texte. Le laisser à false garde les
 * tailles de police d'origine : c'est le choix à faire quand les mentions sont
 * déjà au minimum légal, qu'aucune réduction ne doit toucher.
 *
 * @returns {{template: object, pictosSousMinimum: string[], ratio: object}}
 */
export function mettreALEchelleModele(template, canvas, { adapterCorps = true } = {}) {
    const { rx, ry, ru } = facteurs(template?.canvas, canvas);
    const pictosSousMinimum = [];
    const textesTropPetits = [];
    const TAILLES_TEXTE = ['size', 'letterSpacing', 'strokeWidth',
                           'shadowDistance', 'shadowSize', 'shadowSpread'];

    const layers = (template?.layers || []).map((t) => {
        const n = { ...t };
        if (n.x !== undefined) n.x = n.x * rx;
        if (n.y !== undefined) n.y = n.y * ry;
        if (n.layerType === 'text') {
            if (n.width !== undefined) n.width = n.width * rx;
            if (n.height !== undefined) n.height = n.height * ry;
            if (adapterCorps) {
                for (const k of TAILLES_TEXTE) if (typeof n[k] === 'number') n[k] = n[k] * ru;
                if (typeof n.size === 'number' && n.size < CORPS_MIN_PT) textesTropPetits.push(n.key || n.role);
            }
        } else {
            // Carré préservé : un losange GHS déformé n'est plus un pictogramme
            // homologué.
            if (n.width !== undefined) n.width = n.width * ru;
            if (n.height !== undefined) n.height = n.height * ru;
            if (n.role === 'pictogram' && n.width !== undefined && n.width < PICTO_MIN_MM - 0.05) {
                pictosSousMinimum.push(n.pictogramCode || 'GHS');
            }
        }
        return n;
    });

    return {
        template: { ...template, canvas: { width_mm: canvas.width_mm, height_mm: canvas.height_mm }, layers },
        pictosSousMinimum: [...new Set(pictosSousMinimum)],
        textesTropPetits: [...new Set(textesTropPetits)],
        ratio: { rx, ry, ru },
    };
}

/** Vrai si l'objet ressemble à un modèle .lfgr valide. */
export function isRegulatoryTemplate(obj) {
    return !!obj && obj.type === 'lfgr' && Array.isArray(obj.layers);
}

/**
 * Réconcilie des calques fraîchement importés (fiche réglementaire) avec les
 * calques réglementaires DÉJÀ présents sur l'étiquette — le squelette d'un
 * modèle .lfgr, ou un import précédent DU MÊME PRODUIT.
 *
 * Règle métier : contenu = fiche entrante · placement et mise en forme = existant.
 *
 * Trois choses que cette fonction ne faisait pas, et qui envoyaient de fausses
 * mentions réglementaires à l'impression :
 *
 * 1. **Elle ignorait `blockId`.** L'index était bâti sur TOUS les calques
 *    réglementaires de l'étiquette, par rôle seul. Importer une seconde fiche
 *    (pack deux saveurs) détruisait donc le bloc du premier produit et installait
 *    les textes du second à ses coordonnées. On ne réconcilie plus qu'avec le
 *    MÊME bloc, ou avec le squelette d'un modèle (`source: 'template'`), qui n'a
 *    justement pas de bloc à lui.
 * 2. **Elle laissait les restes du produit précédent.** Un rôle présent avant et
 *    absent de la nouvelle fiche (produit non classé : plus de pictogrammes, plus
 *    d'UFI) restait sur l'étiquette et partait au PDF. Ces calques sortent
 *    désormais par `staleIds` — exactement ce que fait déjà « Rafraîchir ».
 * 3. **Elle détruisait le calque pour le remplacer.** `removeIds` + `addElement`
 *    faisait perdre l'appartenance au groupe, la place dans la pile, le verrou et
 *    le masquage. Quand le type ne change pas — le cas courant — le calque est
 *    maintenant mis à jour SUR PLACE (`updates`), et ne perd rien.
 *
 * @param {object[]} incoming - calques produits par buildRegulatoryLayers
 * @param {object[]} existing - tous les calques de l'étiquette
 * @returns {{adds:object[], updates:{id:string,partial:object}[],
 *            removeIds:string[], staleIds:string[]}}
 */
export function reconcileImportedLayers(incoming, existing, { canvas } = {}) {
    // Bloc du lot entrant : tous les calques d'un import partagent le même.
    const blockIdEntrant = (incoming || []).find((l) => l && l.regBlock)?.regBlock?.blockId || null;

    // Périmètre de la réconciliation. Indexé en LISTES : un rôle peut être porté
    // par plusieurs calques (UFI dupliqué sur un rabat). Avec une valeur unique,
    // seul le dernier était mis à jour et son jumeau gardait, muet, l'ancienne
    // teneur en nicotine.
    const perimetre = (existing || []).filter(
        (e) => e && e.regBlock && !estFicheMasqueeDEssai(e)   // le distributeur masqué d'une étiquette d'essai : ni réécrit ni retiré
            && (e.regBlock.source === 'template' || e.regBlock.blockId === blockIdEntrant)
    );
    const byKey = {};
    for (const e of perimetre) {
        const k = regLayerKey(e);
        (byKey[k] = byKey[k] || []).push(e);
    }

    const adds = [];
    const updates = [];
    const removeIds = [];
    const clesServies = new Set();

    for (const layer of (incoming || [])) {
        const cle = regLayerKey(layer);
        const correspondants = byKey[cle] || [];
        if (correspondants.length === 0) {
            adds.push(layer);
            continue;
        }
        clesServies.add(cle);
        // Chaque exemplaire existant reçoit le contenu entrant, en gardant SA place.
        for (const match of correspondants) {
            if (match.type !== layer.type) {
                // Type différent (ancien picto en `image`, nouveau en `shape` SVG) :
                // pas de mise à jour partielle possible. On remplace, en reprenant
                // l'identité du calque pour ne pas l'arracher à son groupe.
                adds.push({
                    ...layer,
                    ...copiePlacement(match),
                    parentId: match.parentId ?? null,
                    locked: !!match.locked,
                    hidden: !!match.hidden,
                });
                removeIds.push(match.id);
            } else {
                updates.push({ id: match.id, partial: partielDeContenu(layer, match) });
            }
        }
    }

    // Restes : calques du MÊME bloc (ou du squelette) dont le rôle n'est plus
    // produit par la fiche. Les laisser, c'est imprimer l'UFI du produit
    // précédent sur l'étiquette du suivant.
    // Sauf les mentions que seules les zones `print` posent, face à une fiche
    // relue SANS zones : elle ne prouve pas leur disparition (cf. ROLES_DES_ZONES).
    // Gardées telles quelles, et rendues par `conserves` pour que l'appelant le dise.
    const avecZones = lotAvecZones(incoming);
    const nonServis = perimetre
        .filter((e) => !clesServies.has(regLayerKey(e)) && !removeIds.includes(e.id));
    const conserves = nonServis.filter((e) => gardeFauteDeZones(e, avecZones)).map((e) => e.id);
    const staleIds = nonServis.filter((e) => !gardeFauteDeZones(e, avecZones)).map((e) => e.id);

    // ── Les rôles VRAIMENT nouveaux adoptent le bloc en place ──
    // Même situation qu'à l'application d'un modèle, par l'autre porte : on
    // importe une fiche sur le squelette d'un .lfgr (import PDF par lot), et
    // elle apporte une mention que le squelette ne portait pas. Elle arrivait
    // au placement de l'import — Arial 7 pt noir en travers d'un bloc composé.
    // Le bloc qui RESTE après cet import sert de modèle implicite : mêmes
    // règles, même fonction, donc même résultat par les deux portes.
    const restant = perimetre.filter((e) => clesServies.has(regLayerKey(e)));
    if (adds.length && restant.length) {
        const implicite = extractRegulatoryTemplate(restant, canvas);
        const servis = {};
        for (const e of restant) servis[regLayerKey(e)] = (servis[regLayerKey(e)] || 0) + 1;
        // Les nouveaux n'ont pas encore d'identifiant : on en pose un le temps
        // de la passe, et on reverse les patches dans les calques eux-mêmes.
        const provisoires = adds.map((l, i) => ({ ...l, id: l.id || `__neuf${i}` }));
        const patchesNeufs = {};
        adopterOrphelins(implicite, provisoires, servis, patchesNeufs);
        for (let i = 0; i < adds.length; i++) {
            const patch = patchesNeufs[provisoires[i].id];
            if (patch) adds[i] = { ...adds[i], ...patch };
        }
    }

    return { adds, updates, removeIds, staleIds, conserves };
}

/** Placement de l'existant, à reporter sur un calque de remplacement. */
function copiePlacement(match) {
    const out = {};
    for (const k of PLACEMENT_KEYS) if (match[k] !== undefined) out[k] = match[k];
    return out;
}

/**
 * Ce que l'import écrit sur un calque qu'il met à jour : le CONTENU, et rien
 * d'autre — le placement et la mise en forme appartiennent à l'utilisateur.
 *
 * Deux précautions :
 *   - `content: null` sur un texte. Le rendu donne la priorité au rich text sur
 *     `el.text` : sans cette remise à zéro, un calque corrigé une fois au
 *     double-clic n'affichait plus JAMAIS la valeur à jour — il gardait, à
 *     l'écran comme au PDF, l'ancienne teneur en nicotine pendant que le message
 *     annonçait « mis à jour ».
 *   - avant de jeter ce rich text, on PROMEUT en propriétés à plat la couleur,
 *     la police et le corps qu'il portait, sans quoi les régler au double-clic
 *     revenait à les perdre au réimport suivant.
 */
export function partielDeContenu(layer, match) {
    // Le jumeau AromaSense d'une étiquette d'essai garde son texte : la fiche relue ne remet pas le distributeur de la fiche
    // à l'impression. Sa méta suit (fetched_at…), ses marques d'essai restent (le lien vers le distributeur masqué compris).
    if (estJumeauDEssai(match) && layer.regBlock?.role === 'distributor') return { regBlock: { ...layer.regBlock, essai: match.regBlock.essai } };
    const partial = { regBlock: layer.regBlock };
    if (layer.text !== undefined) partial.text = layer.text;
    if (layer.url !== undefined) partial.url = layer.url;
    if (layer.svgContent !== undefined) partial.svgContent = layer.svgContent;
    if (layer.svgViewBox !== undefined) partial.svgViewBox = layer.svgViewBox;
    if (match.type === 'text') {
        const st = styleNormalise(match);
        if (st.color !== undefined) partial.color = st.color;
        if (st.font !== undefined) partial.font = st.font;
        if (st.size !== undefined) partial.size = st.size;
        partial.content = null;
        // Le cadre GRANDIT si le nouveau texte ne tient plus (jamais il ne rétrécit) :
        // l'ajustement au contenu saute les calques verrouillés, et un ancien calque
        // `hazard` qui reçoit la ligne « Danger » des zones s'imprimait coupé (#336).
        // Seulement si le texte CHANGE : l'estimation est large exprès, et elle
        // agrandissait de 15 à 50 % des cadres ajustés à la main dont rien ne
        // changeait, à chaque rafraîchissement et chaque application de modèle
        // (relecture du 19/10).
        if (layer.text !== undefined && layer.text !== match.text && match.width > 0) {
            const besoin = hauteurTexteEstimee(layer.text, match.width, st.size ?? match.size ?? 7, st.font ?? match.font);
            if (!(match.height >= besoin)) partial.height = besoin;
        }
    }
    return partial;
}

/**
 * Instancie les calques d'un modèle (.lfgr) — le « squelette » : texte de départ
 * (guide) + placement + mise en forme. Sert à superposer un modèle sur un PDF
 * fraîchement importé (import par lot). Les pictogrammes sont ré-résolus depuis
 * leur code GHS via `resolvePictogram` (le modèle ne stocke pas le SVG).
 *
 * Les calques renvoyés n'ont PAS d'id — l'appelant en attribue un (crypto.randomUUID).
 *
 * @param {object} template - modèle .lfgr
 * @param {(code:string)=>({svgContent:string,svgViewBox:string}|null)} [resolvePictogram]
 * @returns {Array<object>} calques prêts à insérer (sans id)
 */
export function instantiateRegulatoryTemplate(template, resolvePictogram) {
    const out = [];
    for (const t of (template?.layers || [])) {
        if (!t || !t.role) continue;
        const regBlock = { role: t.role, blockId: 'reg-template', source: 'template' };
        if (t.pictogramCode) regBlock.pictogramCode = t.pictogramCode;

        const base = {
            regBlock,
            contentLocked: true,
            layerColor: '#F97316',
            name: t.name || `REG · ${t.role}`,
            rotation: t.rotation || 0,
        };
        for (const k of PLACEMENT_KEYS) if (t[k] !== undefined) base[k] = t[k];

        if (t.layerType === 'text') {
            const layer = { ...base, type: 'text', text: t.text || '' };
            for (const k of STYLE_KEYS) if (t[k] !== undefined) layer[k] = t[k];
            if (t.content) layer.content = t.content;
            out.push(layer);
        } else if (t.layerType === 'shape' || t.role === 'pictogram') {
            const pic = (resolvePictogram && t.pictogramCode) ? resolvePictogram(t.pictogramCode) : null;
            if (pic && pic.svgContent) {
                out.push({ ...base, type: 'shape', shapeType: 'svg', svgContent: pic.svgContent, svgViewBox: pic.svgViewBox, svgFill: true });
            } else {
                // SVG local indisponible (code exotique) : carré repère orange.
                out.push({ ...base, type: 'shape', shapeType: 'rect', color: '#F97316', borderRadius: 0 });
            }
        } else if (t.layerType === 'image') {
            // Le modèle ne stocke pas l'image source → carré repère.
            out.push({ ...base, type: 'shape', shapeType: 'rect', color: '#F97316', borderRadius: 0 });
        }
    }
    return out;
}
